/**
 * FireButton — a single MIDI button. Renders a circular button styled by
 * the item's color, sends MIDI (CC or note) per the item's actionType +
 * mode (toggle / momentary / trigger).
 *
 * One instance per layout button-item. The previous hardcoded version in
 * App.tsx is now expressed as a default seed in the layout store.
 *
 * Toggle mode (2026-08-16 — moved off client-only state onto OPBridge's
 * stateful `/toggles/{consumer}` engine, per the 2026-08-05 ask: "gotta be
 * robust as much as possible... i need to read this status for other
 * apps"). A tap pushes the button's resolved on/off actions
 * (`opbridge/dispatch.ts::pushToggle`, idempotent) then flips
 * (`flipToggleAction`) — the RETURNED state is what lights the button, never
 * a locally-guessed `!on()`. State now survives the tab closing: a pedal
 * (or another tab) flipping this same toggle id is picked up by
 * `toggleStatePoll.ts` and reflected here on the next poll tick or on
 * reconnect. (2026-08-20: the legacy bridge's `midi.in` echo this doc used
 * to describe as a supplementary signal is removed — `packages/bridge/` is
 * deleted, and that echo had been unreachable for months regardless, since
 * nothing fed it. Momentary/trigger's `on()` was always false in practice
 * either way — no functional change.) Momentary + trigger modes hide the
 * ON/OFF readout entirely since the visible state would be too brief to be
 * useful.
 */

// `on` is aliased: this component already has a local `on()` accessor for its
// lit state, and importing Solid's `on` unaliased silently shadows it.
import { createSignal, createEffect, on as onDep, onCleanup, For, type Accessor } from 'solid-js';
import { midiInLast } from '../midi/inbound.ts';
import {
  fireAction, fanoutTargets, pushToggle, flipToggleAction, type FireResult, type ToggleActionConfig,
  PROGRAM_ALREADY_RUNNING,
} from '../opbridge/dispatch.ts';
import { bridgeConnectedReactive, anyBridgeConnectedReactive, bridgeStateKey } from '../opbridge/store.ts';
import { ulid } from '../utils/ulid.ts';
import { displayRecQuant, displayLaunchQuant } from '@opbuttons/schema';
import { abletonState, clockLastTempo } from '../opbridge/clockPoll.ts';
import { obsState, obsButtonFault, obsFaultLabel, obsSceneIsLive } from '../obs/scenes.ts';
import { abletonDeviceStateFor } from '../ableton/deviceState.ts';
import {
  programState, programButtonFault, programFaultLabel, programIsRunning,
  programIsPending, programEntry, programDisplayName,
} from '../program/programs.ts';
import {
  abletonDeviceFault,
  abletonDeviceFaultLabel,
  abletonDeviceIsOn,
  abletonDeviceSummary,
  effectiveNth,
} from '../ableton/deviceControl.ts';
import { editOwnsGesture, isEditMode, selectedItemId } from '../layout/store.ts';
import type { ButtonItem, LayoutColor } from '../layout/store.ts';
import { activePedals, buttonEmitsMidi, motionPresetById } from '../layout/projectStore.ts';
import { recordButtonRuntime, clearButtonRuntime } from '../layout/buttonRuntime.ts';
import { toggleState, recordToggleState } from '../toggle/runtime.ts';
import { GUARD_ARM_MS } from '../performance/store.ts';
import { clipPathFor, shapeDef } from '../layout/shapes.ts';
import { splitLabelLines, fitPadName } from './autoShrinkLabel.ts';
import { pushToast } from '../ui/toast.ts';
import type { MidiCcStep, MidiNoteStep, MidiProgramChangeStep, AbletonStep } from '@opbuttons/schema';
import { stepLit, stepCanLight } from '../ableton/stepState.ts';
import { buttonFace } from '../ableton/rows.ts';
import { pedalBridgeRowId } from '../pedals/bridgeBinding.ts';
import {
  presetState, presetById, presetButtonFault, presetFaultLabel, presetFireBody,
  presetLayerRunning, markPresetLayer, presetLengthMs,
} from '../lighting/presets.ts';
import { ensurePresets } from '../lighting/presetRead.ts';
import { fireHueScene, findHueScene, hueListSummary, hueSceneLabel, hueSwatchCss, watchHueScenes } from '../lighting/hueScenes.ts';
import { primaryBridge } from '../opbridge/store.ts';
import { timelineLit } from '../timeline/canvasLights.ts';
import { crossfadeSide } from '../timeline/opMotion.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

interface FireButtonProps {
  item: ButtonItem;
}

const OFF_VALUE = 0;

// Map LayoutColor to the existing design token names. Keeping the layout
// store independent of CSS variable spelling means we can rename tokens
// without churning saved layouts.
const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/**
 * Say so when a RESYNC did not leave the building.
 *
 * Only this step kind gets a failure toast, and only on failure. The
 * justification is specific to it rather than a general policy: every other
 * Ableton Control step writes something the operator can SEE in Live (a
 * tempo, a time signature, a lit metronome), so a failed send shows up by
 * its absence. A resync writes nothing observable from here - the slave is a
 * different machine running a device that acks nothing - so a swallowed
 * failure looks exactly like a success that did not help. On a control whose
 * whole job is recovery, that is the worst available outcome.
 *
 * The dominant case by far is `unsupported-step`: a resync that has not been
 * aimed at the slave's bridge, refused before it went anywhere (see
 * `sendAbletonResync`). Its detail is written to be read on stage, so it is
 * shown verbatim rather than being flattened into the kind name - "Resync
 * must name the bridge running on the slave" is actionable;
 * "unsupported-step" is not.
 *
 * The 422 case is kept even though the current design should never produce
 * one, because it costs a line and it is the signature of the send having
 * been pointed off-box again: OPBridge v1.15.125 answers `POST /osc/send`
 * with 422 for ANY non-loopback target (measured 2026-09-12 - same address
 * and args to 127.0.0.1 delivered, to this machine's own LAN address did
 * not). "422 Unprocessable Entity" on stage tells a DJ nothing.
 */
/*
 * Also used for Sustain Kill (2026-09-23), which shares every property that
 * earned this: loopback on an aimed bridge, refused when unaimed, and a
 * device that acks nothing. `what` is the word shown on stage.
 */
function reportResyncFailure(results: FireResult[], what = 'RESYNC'): void {
  const failed = results.filter((r) => r.kind !== 'ok');
  if (failed.length === 0) return;
  const why = failed.map((r) => {
    switch (r.kind) {
      case 'no-bridges':          return 'no bridge paired';
      case 'bridge-unknown':      return 'unknown bridge';
      case 'bridge-disconnected': return 'bridge disconnected';
      case 'unsupported-step':    return r.detail ?? 'refused';
      case 'http-error':
        return (r.detail ?? '').includes('422')
          ? 'OPBridge refused it - it only relays OSC to itself, not across the LAN'
          : `send failed (${r.detail ?? 'error'})`;
      default:                    return r.kind;
    }
  });
  pushToast('error', `${what} not sent - ${[...new Set(why)].join('; ')}`);
}

export function FireButton(props: FireButtonProps) {
  const [on, setOn] = createSignal(false);
  const [pressed, setPressed] = createSignal(false);
  // 2026-07-28: the pressed VISUAL used to be a flat 120ms flash from
  // pointerdown for every mode. For MOMENTARY that is wrong - the button
  // sends note-on at pointerdown and note-off at pointerup, so a held
  // button kept sounding while already looking released. The visual now
  // tracks the finger for momentary, with a 120ms floor so a quick stab
  // still registers. Toggle / trigger keep the flash: their press really
  // is instantaneous.
  let pressedAtMs = 0;
  function beginPress(): void { pressedAtMs = performance.now(); setPressed(true); }
  function endPressWithFloor(): void {
    const held = performance.now() - pressedAtMs;
    const remaining = Math.max(0, 120 - held);
    if (remaining === 0) setPressed(false);
    else setTimeout(() => setPressed(false), remaining);
  }

  /**
   * Light the button when its OWN bound MIDI trigger fires (operator ask,
   * 2026-08-28: "it'd be cool to see the button in opbuttons presses when the
   * midi triggers").
   *
   * ⚠️ **Purely visual - this must never dispatch.** An A-path binding is
   * executed by OPBridge's rules engine, not by this tab, so firing here would
   * send every message TWICE whenever the tab happens to be open. The whole
   * point of A-path is that the action does not depend on us.
   *
   * Feeds off `midiInLast`, the inbound stream restored at CP-111, so this
   * needs no new plumbing and works for any bound control on any port.
   *
   * Match is kind + number + channel. **`MidiInEvent` carries no port**, so a
   * second device sending the identical message on a different port would also
   * flash this button. Accepted: the consequence is a spurious 120ms highlight,
   * never a spurious send, and the rule OPBridge matches is port-scoped
   * (`portEquals`) so the actual firing stays correct.
   *
   * Momentary tracks the real edges - note-on/CC-non-zero holds it down,
   * note-off/CC-0 releases it - so a held pedal looks held. Every other mode
   * flashes, matching what a finger tap does.
   */
  createEffect(onDep(midiInLast, (ev) => {
    if (!ev) return;
    const b = props.item.midiTrigger;
    if (!b) return;
    const isNote = b.match.type === 'note';
    const kindMatches = isNote ? (ev.kind === 'noteon' || ev.kind === 'noteoff') : ev.kind === 'cc';
    if (!kindMatches) return;
    if (ev.number !== b.match.number || ev.channel !== b.match.channel) return;

    // Release edge: an explicit note-off, a velocity-0 note-on (MIDI's other
    // note-off convention), or CC value 0 - the same partition `ruleSync`'s
    // release condition uses, so the visual cannot disagree with the wire.
    const isRelease = isNote ? (ev.kind === 'noteoff' || ev.value === 0) : ev.value === 0;

    if (effectiveMode() === 'momentary') {
      if (isRelease) endPressWithFloor();
      else beginPress();
      return;
    }
    if (isRelease) return;  // other modes have nothing to show on release
    beginPress();
    setTimeout(() => setPressed(false), 120);
  }, { defer: true }));

  // Show-mode guard (SD-5): first tap arms for GUARD_ARM_MS, second tap
  // fires. Only active in performance mode on a guarded button; edit + play
  // fire on first tap as always.
  const [armed, setArmed] = createSignal(false);
  let armTimer: number | undefined;
  function armGuard(): void {
    setArmed(true);
    if (armTimer !== undefined) clearTimeout(armTimer);
    armTimer = window.setTimeout(() => { armTimer = undefined; setArmed(false); }, GUARD_ARM_MS);
  }
  function disarmGuard(): void {
    if (armTimer !== undefined) { clearTimeout(armTimer); armTimer = undefined; }
    setArmed(false);
  }
  onCleanup(() => { if (armTimer !== undefined) clearTimeout(armTimer); });
  // Entering edit mode clears any stale armed state (edit ignores guards, SD-5).
  createEffect(() => { if (isEditMode()) disarmGuard(); });

  // Mirror the lit state out to the shared button-runtime store so a
  // DisplayItem bound to this button can read it (source: 'button'). Pure
  // additive write-out - reads the already-computed `on()` signal, writes
  // to an external store, changes nothing about this component's own
  // rendering or dispatch.
  createEffect(() => { recordButtonRuntime(props.item.id, on()); });
  onCleanup(() => clearButtonRuntime(props.item.id));

  /** The pedal this button is bound to (Guitar FX Preset, DEC-152 amended
   *  2026-08-04), resolved LIVE every read — never cached on the item.
   *  `undefined` for every ordinary button (the overwhelming majority),
   *  which never sets `pedalId` at all. */
  const boundPedal = () => {
    const id = props.item.pedalId;
    return id ? activePedals().find((pd) => pd.id === id) : undefined;
  };
  /** A `pedalId` that no longer resolves (the device was removed from
   *  Connected Devices) is a loud dead state, per DEC-124/138 — never a
   *  silent fall-back to the item's own stale channel/port fields. */
  const pedalMissing = () => !!props.item.pedalId && !boundPedal();
  /** channel/port for the wire: a bound pedal's LIVE values when one
   *  resolves, otherwise the item's own fields (every ordinary button). */
  const resolvedChannel = () => boundPedal()?.channel ?? props.item.channel;
  /** Pedal-bound: the pedal's own bridge (single target, inherited live —
   *  never the item's own `bridgeIds`, which only applies to ordinary
   *  buttons). Ordinary: the item's configured `bridgeIds` (multi-select
   *  fan-out, 2026-08-13) — undefined means the default bridge. */
  const targets = (): string[] =>
    boundPedal() ? [pedalBridgeRowId(boundPedal()) ?? ''] : fanoutTargets(props.item.bridgeIds);
  /** True when AT LEAST ONE of the button's actual send targets is
   *  connected. Drives the disabled state + early-return guard in fire().
   *  Reactive so the button enables the moment a target connects. (Legacy
   *  Node-bridge dispatch removed 2026-07-18.) */
  const anyTransportLive = () =>
    boundPedal() ? bridgeConnectedReactive(pedalBridgeRowId(boundPedal())) : anyBridgeConnectedReactive(props.item.bridgeIds);
  const live = () => anyTransportLive() && !pedalMissing();

  /** Low-level send: emits a single ON or OFF event on both transports and
   *  resolves with the dispatch result. For toggles, the caller passes
   *  `turningOn = !on()`; for momentary / trigger, the caller passes the
   *  literal intent. CC mode treats OFF as value=0; noteon mode treats OFF
   *  as note-off (or noteon vel=0 on the OPBridge HTTP path, which DAWs
   *  accept as equivalent). */
  async function sendOnce(turningOn: boolean): Promise<FireResult[]> {
    const action = props.item.actionType ?? 'cc';
    // Named-port override (DEC-149 pattern extended to Button, then to a
    // live pedal reference 2026-08-04) — a Guitar FX pedal lives on its own
    // port, not the rig default. Spread in conditionally rather than
    // `port: boundPedal()?.port`: the schema's `MidiStepBase.port` is a
    // bare `?:`, so assigning it an explicit `undefined` would fail under
    // `exactOptionalPropertyTypes`.
    const port = boundPedal()?.port;
    const channel = resolvedChannel();
    // Fan out to every configured target (pedal-bound = one, ordinary =
    // the item's bridgeIds — possibly several, possibly zero).
    const ids = targets();
    if (action === 'ableton') {
      // Like `pc`, an Ableton Control step has no OFF counterpart - setting a
      // tempo is not something you un-set - so swallow the release rather
      // than firing the whole chain a second time on pointerup.
      if (!turningOn) return [];
      const steps = props.item.abletonSteps ?? [];
      if (steps.length === 0) return [];
      const results: FireResult[][] = [];
      // SEQUENTIAL, not Promise.all. "140, 6/8, rec quant off" is one tap but
      // several sends, and firing them concurrently would race a burst of UDP
      // datagrams at one `udpreceive` with no ordering guarantee. Awaiting in
      // order also means a rejected step (a bad quant label) is reported
      // against the step that caused it rather than lost in a combined
      // result. The chain is 1-6 short HTTP calls; the cost is not felt.
      for (const step of steps) {
        for (const target of ids) {
          // `target` selects the BRIDGE. A practice-clock step ignores it
          // entirely - dispatch intercepts those before bridge resolution -
          // so fanning one out across N bridges would apply it N times to a
          // single local signal. Harmless (the writes are idempotent) but
          // pointless, so send it once.
          const r = await fireAction({ ...step, target });
          results.push(r);
          // A resync is a RECOVERY action: the operator hits it because the
          // lights are already drifting, and the device acks nothing, so a
          // refused send that we swallow is indistinguishable from a
          // successful one that did not help. Every other Ableton step can
          // be checked by looking at Live; this one cannot. Toast only on
          // failure - a confirmation on success would be the arm/mirror
          // ceremony this control was deliberately built without.
          if (step.type === 'ableton.resync') reportResyncFailure(r);
          if (step.type === 'ableton.sustainkill') reportResyncFailure(r, 'SUSTAIN KILL');
          if ('clock' in step && step.clock === 'practice') break;
        }
      }
      return results.flat();
    }
    if (action === 'obs') {
      // A scene switch has no OFF - you cannot un-switch to a scene - so the
      // release is swallowed exactly as `ableton` and `pc` swallow theirs.
      if (!turningOn) return [];
      const scene = props.item.obsScene ?? '';
      if (!scene) return [];
      // SEQUENTIAL across bridges, like the Ableton chain. In practice `ids`
      // is one bridge (OBS runs on one machine), but a fan-out here would be
      // several full obs-websocket handshakes racing each other, and the
      // per-bridge failure detail is what the face reads - losing which
      // bridge refused would make the dead state unreadable.
      const results: FireResult[][] = [];
      for (const target of ids) {
        results.push(await fireAction({
          type: 'obs.scene',
          id: ulid(),
          target,
          scene,
        }));
      }
      return results.flat();
    }
    if (action === 'runProgram') {
      // Running a program has no OFF (OPBridge has no stop endpoint, by
      // design - DEC-524), so the release is swallowed like a scene switch.
      if (!turningOn) return [];
      const programId = props.item.runProgramId ?? '';
      if (!programId) return [];
      const results: FireResult[] = [];
      for (const target of ids) {
        results.push(...await fireAction({
          type: 'program.launch',
          id: ulid(),
          target,
          programId,
        }));
      }
      // A failed launch must say so on stage. The face covers the states the
      // poll can see (no access, not on that computer); this covers the ones
      // it cannot, above all a registration whose path is wrong, which
      // OPBridge answers 500 "spawn failed" and which would otherwise look
      // exactly like a press that did nothing.
      // "Already running" is not a failure - the program you asked for is
      // up - so it gets its own plain, non-error wording. Seen in the first
      // end-to-end run, which toasted "not started - Already running".
      const failed = results.filter((r) => r.kind !== 'ok' && r.detail !== PROGRAM_ALREADY_RUNNING);
      if (failed.length > 0) {
        pushToast('error', `${programFaceName()} not started - ${[...new Set(failed.map((r) => r.detail ?? r.kind))].join('; ')}`);
      } else if (results.some((r) => r.detail === PROGRAM_ALREADY_RUNNING)) {
        pushToast('warn', `${programFaceName()} is already running`);
      }
      return results;
    }
    if (action === 'hueScene') {
      // Recalling a scene is a one-shot (there is no un-recall), so the release is swallowed, as a scene switch's is.
      if (!turningOn) return [];
      const ref = props.item.hueScene;
      if (!ref?.scene) return [{ target: ids[0] ?? '<default>', kind: 'unsupported-step', detail: 'No scene chosen - pick one in the Inspector.' }];
      const row = hueRowId();
      if (!row) return [{ target: '<default>', kind: 'no-bridges', detail: 'Pick a computer for it in the Inspector.' }];
      const results = await fireHueScene(row, ref);
      const failed = results.filter((r) => r.kind !== 'ok');
      if (failed.length) pushToast('error', `${hueSceneLabel(ref)} not recalled - ${[...new Set(failed.map((r) => r.detail ?? r.kind))].join('; ')}`);
      return results;
    }
    if (action === 'lightPreset') {
      // Press PLAYS the preset as this button's own layer (layer id = item
      // id, so re-pressing replaces it in place rather than stacking a
      // second copy); release STOPS it. Trigger mode is a one-shot, so its
      // release - which arrives in the same tick - is swallowed: stopping
      // there would end the show the press just started.
      if (!turningOn && effectiveMode() === 'trigger') return [];
      const cfg = props.item.lightPreset;
      if (!cfg?.presetId) {
        if (!turningOn) return [];
        return [{ target: ids[0] ?? '<default>', kind: 'unsupported-step', detail: 'No preset chosen - pick one in the Inspector.' }];
      }
      const body = presetFireBody(props.item.id, cfg);
      const results: FireResult[] = [];
      for (const target of ids) {
        results.push(...await fireAction(turningOn
          ? {
              type: 'lighting.fire', id: ulid(), target, layerId: props.item.id, preset: body.preset,
              ...(body.knobs ? { knobs: body.knobs } : {}),
              ...(body.fixtures ? { fixtures: body.fixtures } : {}),
              ...(body.lengthBeats !== undefined ? { lengthBeats: body.lengthBeats } : {}),
            }
          : { type: 'lighting.stop', id: ulid(), target, layerId: props.item.id }));
      }
      // Lit = what WE started and have not stopped (OPBridge has no read of
      // running layers). Only a clean result moves it: a refused fire must
      // not light the button, and a refused stop must not dark it.
      if (results.length > 0 && results.every((r) => r.kind === 'ok')) {
        // A timed layer ends by itself on OPBridge; go dark when it does.
        markPresetLayer(props.item.id, turningOn,
          turningOn && cfg.lengthBeats !== undefined ? presetLengthMs(cfg.lengthBeats, clockLastTempo()) : undefined);
        // It played, but some of its lights are held by someone else (OPBridge S297 `parked`; today OPStage's Send
        // holds them all), so they show nothing. Said, instead of a press that lights nothing and says nothing.
        const parked = results.flatMap((r) => r.parked ?? []);
        if (turningOn && parked.length) {
          const holder = (by: string) => (by.toLowerCase() === 'opstage' ? 'OPStage' : by);
          const byHolder = new Map<string, string[]>();
          for (const p of parked) byHolder.set(holder(p.by), [...(byHolder.get(holder(p.by)) ?? []), p.light]);
          const parts = [...byHolder].map(([by, ls]) => `${ls.join(', ')} ${ls.length === 1 ? 'is' : 'are'} held by ${by}`);
          pushToast('warn', `${presetFaceName() || 'Light preset'} played, but ${parts.join('; ')}, so ${parked.length === 1 ? 'it shows' : 'they show'} nothing until let go.`);
        }
      } else {
        const failed = results.filter((r) => r.kind !== 'ok');
        pushToast('error', `${presetFaceName() || 'Light preset'} ${turningOn ? 'not played' : 'not stopped'} - ${[...new Set(failed.map((r) => r.detail ?? r.kind))].join('; ')}`);
      }
      return results;
    }
    if (action === 'abletonDevice') {
      const target = props.item.abletonDevice;
      // ⚠️ The `cc` fall-through below is still the hazard this early return
      // exists for: without it a device button would send
      // `[0xB0|ch, ccNumber, onValue]` from the placeholder `ccNumber: 0`
      // such an item carries - cc 0 at Ableton, from a button labelled as a
      // device toggle. That is not hypothetical; it is the bug found in the
      // timeline path on 2026-09-19.
      if (!target?.trackName || !target.deviceName) {
        if (!turningOn) return [];
        return [{
          target: targets()[0] ?? '<default>',
          kind: 'unsupported-step',
          detail: 'This Ableton Device button has no track or device set - choose them in the Inspector.',
        }];
      }
      // The desired value is resolved HERE, against the last observed device
      // state, rather than latched on the button. `on()` is itself driven by
      // the readback effect above, so this inverts what Live last reported -
      // which is why a device somebody switched by hand in Live still
      // toggles the right way on the next press.
      const want = !abletonDeviceIsOn(target, deviceLive());
      const ids = targets();
      const results: FireResult[][] = [];
      for (const bridgeTarget of ids) {
        results.push(await fireAction({
          type: 'abletonDevice.setOn',
          id: ulid(),
          target: bridgeTarget,
          trackName: target.trackName,
          deviceName: target.deviceName,
          ...(target.nth !== undefined ? { nth: effectiveNth(target) } : {}),
          on: want,
        }));
      }
      return results.flat();
    }
    if (action === 'pc') {
      // Program Change has no OFF. `isTrigger()` already forces the trigger
      // path for pc buttons, but momentary's pointerup still calls through
      // here with `false` — swallow it rather than sending a second PC,
      // which would re-fire the same preset on release.
      if (!turningOn) return [];
      const results = await Promise.all(ids.map((target) => {
        const step: MidiProgramChangeStep = {
          type: 'midi.pc',
          id: ulid(),
          target,
          channel: channel + 1,
          program: props.item.program ?? 0,
          ...(port ? { port } : {}),
        };
        return fireAction(step);
      }));
      return results.flat();
    }
    const isNoteMode = action === 'noteon';
    if (isNoteMode) {
      const note = props.item.note ?? 60;
      const velocity = props.item.velocity ?? 127;
      const results = await Promise.all(ids.map((target) => {
        const step: MidiNoteStep = {
          type: 'midi.note',
          id: ulid(),
          target,
          channel: channel + 1,
          note,
          velocity: turningOn ? velocity : 0,
          duration: 0,
          ...(port ? { port } : {}),
        };
        return fireAction(step);
      }));
      return results.flat();
    }
    const value = turningOn ? props.item.onValue : (props.item.offValue ?? OFF_VALUE);
    const results = await Promise.all(ids.map((target) => {
      const step: MidiCcStep = {
        type: 'midi.cc',
        id: ulid(),
        target,
        channel: channel + 1,
        cc: props.item.ccNumber,
        value,
        ...(port ? { port } : {}),
      };
      return fireAction(step);
    }));
    return results.flat();
  }

  /** This button's toggle action-config, read fresh every call — live-
   *  tracking, not a frozen snapshot, same contract as
   *  `midiTrigger/ruleSync.ts`'s `actionFor`, so an Inspector edit (CC
   *  number, channel, onValue) is picked up without a re-Learn step.
   *
   *  `null` for `pc` (no on/off pair) and for every action that emits no
   *  MIDI - asked through `buttonEmitsMidi`, IDENTICALLY to
   *  `ruleSync.ts::buttonToggleConfig`. It used to refuse only `pc`, which
   *  was complete when `pc` was the only action `effectiveMode()` forced to
   *  trigger. OBS, Ableton Control and Ableton Device were forced to trigger
   *  later and fell through to the CC branch, so the mount-time push below
   *  would register a meaningless CC toggle for them on OPBridge whenever
   *  `mode` was unset (the UI sets it; an import or the MCP `add` tool need
   *  not). ruleSync had already been fixed for the same hole; this side had
   *  not. Found 2026-09-22 (S281). */
  function toggleConfig(): ToggleActionConfig | null {
    const action = props.item.actionType ?? 'cc';
    if (action === 'pc' || !buttonEmitsMidi(action)) return null;
    return action === 'noteon'
      ? { actionType: 'noteon', channel: resolvedChannel(), note: props.item.note ?? 60, velocity: props.item.velocity ?? 127 }
      : {
          actionType: 'cc',
          channel: resolvedChannel(),
          ccNumber: props.item.ccNumber,
          onValue: props.item.onValue,
          offValue: props.item.offValue ?? OFF_VALUE,
        };
  }

  // Push tracking shared between the reactive debounce below AND
  // fireToggle's self-heal path — both MUST update the same fingerprint,
  // or they race: the debounce's timer, armed at mount, can fire AFTER a
  // tap's self-heal push already landed, see a stale null fingerprint,
  // and push again — silently resetting `state` back to false with no
  // flip afterward to fix it (the button would show lit while OPBridge's
  // real truth was off, until the next tap). Caught in review before it
  // shipped, not by a failing smoke.
  let toggleSyncTimer: ReturnType<typeof setTimeout> | undefined;
  let lastPushedToggleFingerprint: string | null = null;

  function toggleFingerprint(cfg: ToggleActionConfig, ids: string[]): string {
    return JSON.stringify({ name: props.item.name, cfg, ids });
  }

  /** Push now (bypassing the debounce) and record the fingerprint so the
   *  reactive effect below won't redundantly re-push the same config. */
  async function pushToggleNow(cfg: ToggleActionConfig, ids: string[]): Promise<void> {
    if (toggleSyncTimer !== undefined) { clearTimeout(toggleSyncTimer); toggleSyncTimer = undefined; }
    lastPushedToggleFingerprint = toggleFingerprint(cfg, ids);
    await Promise.all(ids.map((id) => pushToggle(props.item.id, props.item.name, cfg, id)));
  }

  // Reactive push: keeps this button's toggle DEFINITION in sync with
  // OPBridge whenever its config changes — deliberately decoupled from the
  // tap handler. Pushing on every tap was the first version of this and it
  // was a real, caught bug: PUT is a full replace of the
  // `{onAction,offAction,state}` record (byte-verified live 2026-08-16), so
  // a push with no `state` field silently reset a flipped-true toggle back
  // to false right before every flip — every tap after the first became a
  // no-op false→true flip forever. `fire_button_taps_cc.ts`'s "tap again"
  // step caught it; see `pushToggle`'s doc comment for the full story.
  // Debounced 1s, same shape as `midiTrigger/ruleSync.ts`'s DEBOUNCE_MS.
  createEffect(() => {
    if ((props.item.mode ?? 'toggle') !== 'toggle') return;
    const cfg = toggleConfig();
    if (!cfg) return;
    const ids = targets();
    if (ids.length === 0) return;
    const fingerprint = toggleFingerprint(cfg, ids);
    if (fingerprint === lastPushedToggleFingerprint) return;
    if (toggleSyncTimer !== undefined) clearTimeout(toggleSyncTimer);
    toggleSyncTimer = setTimeout(() => { void pushToggleNow(cfg, ids); }, 1000);
  });
  onCleanup(() => { if (toggleSyncTimer !== undefined) clearTimeout(toggleSyncTimer); });

  /** Toggle mode only (DEC-124 Clause B policy carried forward: the lit
   *  face follows the DISPATCH RESULT, never a speculative guess — on any
   *  failure it is left exactly where it was). 2026-08-16: the dispatch is
   *  now OPBridge's stateful toggle engine, not a raw CC/note send. Flips
   *  first (the definition is kept current by the reactive push above, NOT
   *  here); on a 404 (`notFound` — this id isn't in the library yet, e.g.
   *  fresh boot or the debounce above hasn't fired) pushes once via the
   *  SAME `pushToggleNow` the debounce uses (so the two paths can't race
   *  each other) and retries exactly once. Lights the button from the
   *  RETURNED state — never `!on()` computed locally, since the whole
   *  point is that OPBridge, not this tab, is the source of truth. */
  async function fireToggle(): Promise<void> {
    const cfg = toggleConfig();
    if (!cfg) return;
    const ids = targets();
    if (ids.length === 0) return;
    let flipResults = (await Promise.all(ids.map((id) => flipToggleAction(props.item.id, id)))).flat();
    if (flipResults.some((r) => r.notFound)) {
      await pushToggleNow(cfg, ids);
      flipResults = (await Promise.all(ids.map((id) => flipToggleAction(props.item.id, id)))).flat();
    }
    if (flipResults.length > 0 && flipResults.every((r) => r.kind === 'ok')) {
      // Single-bridge is the overwhelmingly common case (fan-out to N
      // independent per-bridge toggle libraries is a real but rare edge
      // case) — light the button from the first target's state, and log
      // (never silently paper over) if targets disagree.
      const state = flipResults[0]!.state ?? false;
      setOn(state);
      recordToggleState(props.item.id, state);
      if (flipResults.some((r) => r.state !== state)) {
        // eslint-disable-next-line no-console
        console.warn(`[toggle] "${props.item.id}" fan-out targets disagree on resulting state`, flipResults);
      }
    }
  }

  /** Syncs `on()` from OPBridge's real toggle state whenever it changes —
   *  the reconnect / pedal-flipped-while-closed case the whole rebuild was
   *  for. Only applies once real server truth exists (`toggleState()`
   *  returns `undefined` until the first successful poll of THIS id) so a
   *  bare PWA with no bridge connected still falls back to the local
   *  optimistic/echo behavior below rather than latching on `false`.
   *  Checks `props.item.mode` directly (not the later-defined
   *  `effectiveMode()`) purely to avoid a forward reference — behaviourally
   *  identical, since a `pc` or non-MIDI button never pushes a toggle to OPBridge in the first place (`toggleConfig()`
   *  returns `null`), so `toggleState()` stays `undefined` for it
   *  regardless of `mode`. [Widened 2026-09-22 from "a `pc`-action button":
   *  that sentence was the whole justification and had gone stale.] */
  createEffect(() => {
    if ((props.item.mode ?? 'toggle') !== 'toggle') return;
    const state = toggleState(props.item.id);
    if (state === undefined) return;
    setOn(state);
  });

  /** The single Ableton Control step this button carries, when it carries
   *  exactly one. A chain ("140 BPM, then 6/8, then rec quant off") has no
   *  single on/off meaning, so it stays a plain trigger. */
  const soleAbletonStep = () => {
    if (props.item.actionType !== 'ableton') return null;
    const steps = props.item.abletonSteps ?? [];
    return steps.length === 1 ? steps[0]! : null;
  };
  /** True when this button's only job is Live's metronome - the one Ableton
   *  Control that HAS a persistent on/off state to reflect. */
  const isMetronomeButton = () => soleAbletonStep()?.type === 'ableton.metronome';

  /** True for an OBS Scene button. */
  const isObsButton = () => (props.item.actionType ?? 'cc') === 'obs';

  /** The bridge whose OBS this button drives. One only: OBS is per-machine,
   *  and a button aimed at several bridges would be reading several
   *  different OBS instances into one lit state. `targets()` resolves
   *  `bridgeIds` (absent = the default bridge) the same way the fire path
   *  does, so the face and the send can never disagree about which machine
   *  they mean. */
  const obsBridgeId = () => bridgeStateKey(targets()[0]);

  /** This button's OBS readback, from `obs/scenes.ts`. */
  const obsLive = () => obsState(obsBridgeId());

  /**
   * Why an OBS button shows state at all: same rule as the metronome above.
   * It FIRES as a trigger (a scene switch has no OFF), but the thing it
   * commands has a real, externally-owned on/off - "is my scene the live
   * one" - which OBS itself, a Stream Deck, or another operator can change
   * without us. A local latch would be wrong the moment any of them did.
   *
   * So the lit state is ALWAYS the readback, never our own intent, and an
   * unread bridge stays dark rather than guessing. That is the same
   * `intent never masquerades as confirmed truth` rule the Rig State
   * feedback design locked for closed-loop targets.
   */
  const obsIsLive = () => obsSceneIsLive(props.item.obsScene, obsLive());

  /** Why this button cannot fire, or null. The ordering rationale lives
   *  with the decision in `obs/scenes.ts::obsButtonFault`, where it can be
   *  tested; this is only the binding. */
  const obsDeadState = (): string | null => {
    if (!isObsButton()) return null;
    const fault = obsButtonFault(props.item.obsScene, props.item.bridgeIds, obsLive());
    return fault === null ? null : obsFaultLabel(fault);
  };

  /**
   * Light a one-step Ableton Control from what Live REPORTS, never from a
   * local latch (DEC-291 for every kind; the metronome since 2026-08-27).
   *
   * The metronome has THREE writers - this button, Live's own transport-bar
   * icon and OPBridge's dashboard - and every other value has at least two
   * (Live's own window, OPSetlist), so a local latch would be wrong the
   * moment any of the others moved it. A "1/16" button lights while Live is
   * on 1/16, "120 BPM" while Live is at 120, Play while it plays, Stop while
   * it is stopped; the answer is `ableton/stepState.ts`, the one a full row's
   * cells read, so a button and the row it came from can never disagree. The
   * metronome keeps its own reading: lit while the click is on.
   *
   * ONE effect owns `on()` for every Ableton Control button and always
   * writes it, so the light is a pure function of the step and the report.
   * (Today an Inspector edit replaces the item and the canvas mounts a fresh
   * button anyway; this does not rely on that.) Not knowing reads as unlit;
   * a chain, Continue, Resync and Sustain kill have no value to show.
   *
   * The face lights (`data-on`); only the metronome grows an ON/OFF pip
   * (`showState`). Under "1/16" an OFF would read as "1/16 is broken", where
   * a row's unlit cells say only "Live is elsewhere".
   */
  createEffect(() => {
    if (props.item.actionType !== 'ableton') return;
    const s = soleAbletonStep();
    if (s && s.type === 'ableton.metronome') {
      setOn(abletonState().metronome?.on ?? false);
      return;
    }
    setOn(!!s && stepCanLight(s) && stepLit(s) === true);
  });

  /** The same rule, one authority along: an OBS button lights from the live
   *  program scene, never from a local latch. An unread or failed bridge
   *  leaves it dark, which is honest - we do not know. */
  createEffect(() => {
    if (!isObsButton()) return;
    setOn(obsIsLive());
  });

  /** True for a Run Program button. */
  const isProgramButton = () => (props.item.actionType ?? 'cc') === 'runProgram';

  /** The bridge whose program this button runs. One only: program ids are
   *  per-machine. Row id, never the stored node id - see `bridgeStateKey`. */
  const programBridgeId = () => bridgeStateKey(targets()[0]);

  /** This button's program-list readback, from `program/programs.ts`. */
  const programLive = () => programState(programBridgeId());

  /** Why this button cannot fire, or null. Ordering lives in
   *  `program/programs.ts::programButtonFault`, where it is tested. */
  const programDeadState = (): string | null => {
    if (!isProgramButton()) return null;
    const fault = programButtonFault(props.item.runProgramId, props.item.bridgeIds, programLive());
    return fault === null ? null : programFaultLabel(fault);
  };

  /** Waiting for the operator to approve the launch on that machine. */
  const programPending = () =>
    isProgramButton() && programIsPending(programBridgeId(), props.item.runProgramId);

  /** The program's name as registered on that machine, else its id. */
  const programFaceName = (): string => {
    const e = programEntry(props.item.runProgramId, programLive());
    return e ? programDisplayName(e) : (props.item.runProgramId ?? '');
  };

  /** Lit = the program is RUNNING on that machine, per OPBridge. Never our
   *  own intent: it can be quit or crash over there with nobody pressing
   *  anything here, and the poll is what turns the light off. */
  createEffect(() => {
    if (!isProgramButton()) return;
    setOn(programIsRunning(props.item.runProgramId, programLive()));
  });

  /** True for a Hue Scene button (2026-10-23, `lighting/hueScenes.ts`). */
  const isHueButton = () => (props.item.actionType ?? 'cc') === 'hueScene';
  /** The computer it fires through: the one it is aimed at, else (never aimed) the one that lists its Hue bridge,
   *  else the primary. An explicit None (`bridgeIds: []`) fires nowhere. Row id. */
  const hueRowId = (): string => {
    if (props.item.bridgeIds !== undefined && props.item.bridgeIds.length === 0) return '';
    return bridgeStateKey(targets()[0]) || findHueScene(props.item.hueScene)?.rowId || primaryBridge()?.id || '';
  };
  /** Keep the scene lists fresh while this button is on screen (shared by every Hue button). */
  createEffect(() => {
    if (!isHueButton()) return;
    const release = watchHueScenes();
    onCleanup(release);
  });
  /** Lit = its scene is its room's active one, as OPBridge last read it (never our own intent: the Hue app, a
   *  switch on the wall or another scene can change it with nobody pressing anything here). */
  createEffect(() => {
    if (!isHueButton()) return;
    setOn(!!findHueScene(props.item.hueScene)?.scene.active);
  });
  /** Why this button cannot recall its scene, or null. */
  const hueDeadState = (): string | null => {
    if (!isHueButton()) return null;
    if (!props.item.hueScene?.scene) return 'pick a scene';
    if (findHueScene(props.item.hueScene)) return null;
    const sum = hueListSummary();
    if (sum.state === 'blocked') return 'no Hue access';
    if (sum.state === 'ok' || sum.state === 'none') return 'not in the Hue app';
    return null;
  };
  /** A Hue Scene button's info line: why not, else "Studio: Stream1". */
  const hueFaceLabel = (): string => hueDeadState() ?? hueSceneLabel(props.item.hueScene ?? { scene: '' });
  /** The scene's own colours (as last read, else as stored), drawn along the face: colour as light. */
  const hueSwatch = (): string | undefined => (isHueButton() ? hueSwatchCss(findHueScene(props.item.hueScene)?.scene.swatch ?? props.item.hueScene?.swatch) : undefined);

  /** True for a Light Preset button. */
  const isPresetButton = () => (props.item.actionType ?? 'cc') === 'lightPreset';

  /** The bridge whose lights this button plays on. One only: a preset runs
   *  on the lights wired to the bridge it is sent to. Row id - see
   *  `bridgeStateKey`. */
  const presetBridgeId = () => bridgeStateKey(targets()[0]);

  /** This button's preset catalogue readback, from `lighting/presets.ts`. */
  const presetLive = () => presetState(presetBridgeId());

  /** Read the catalogue when this button first needs it, and again when its
   *  bridge comes back after a failed read. Nothing polls it: the list only
   *  changes when OPBridge is updated. */
  createEffect(() => {
    if (!isPresetButton()) return;
    const id = presetBridgeId();
    if (!id || !bridgeConnectedReactive(id)) return;
    ensurePresets(id);
  });

  /** Why this button cannot fire, or null. Ordering lives in
   *  `lighting/presets.ts::presetButtonFault`. */
  const presetDeadState = (): string | null => {
    if (!isPresetButton()) return null;
    const fault = presetButtonFault(props.item.lightPreset, props.item.bridgeIds, presetLive());
    return fault === null ? null : presetFaultLabel(fault);
  };

  /** The preset's name as that bridge lists it, else its id. */
  const presetFaceName = (): string => {
    const id = props.item.lightPreset?.presetId ?? '';
    return presetById(id, presetLive())?.name ?? id;
  };

  /** Lit = a layer this app started and has not stopped. OPBridge has no
   *  read of running layers, so this is our own record - rendered as ON,
   *  never as "pending" (CP-159's no-readback rule). A Blackout from this
   *  app clears it; one from elsewhere cannot be seen, and the next press
   *  re-plays and corrects it. */
  createEffect(() => {
    if (!isPresetButton()) return;
    setOn(presetLayerRunning(props.item.id));
  });

  /** True for an Ableton Device button. */
  const isDeviceButton = () => (props.item.actionType ?? 'cc') === 'abletonDevice';

  /** The bridge whose Ableton this button drives. One only, and for a
   *  sharper reason than OBS: the device address is loopback, so a button
   *  aimed at several bridges would be asking several different Live sets
   *  and lighting from whichever answered last. */
  const deviceBridgeId = () => bridgeStateKey(targets()[0]);

  /** This button's device readback, from `ableton/deviceState.ts`. */
  const deviceLive = () => abletonDeviceStateFor(deviceBridgeId());

  /** Why this button cannot fire, or null. The ordering rationale lives with
   *  the decision in `ableton/deviceControl.ts`, where it is tested; this is
   *  only the binding. */
  const deviceDeadState = (): string | null => {
    if (!isDeviceButton()) return null;
    const fault = abletonDeviceFault(
      props.item.abletonDevice, props.item.bridgeIds, deviceLive(),
    );
    return fault === null ? null : abletonDeviceFaultLabel(fault);
  };

  /** Same rule again, third authority: an Ableton Device button lights from
   *  what the device REPORTED, never from a local latch. A mouse click on
   *  the device in Live is exactly the drift this exists to follow, and it
   *  is why a latch was refused here. */
  createEffect(() => {
    if (!isDeviceButton()) return;
    setOn(abletonDeviceIsOn(props.item.abletonDevice, deviceLive()));
  });

  function onPointerDown(e: PointerEvent & { currentTarget: HTMLButtonElement }) {
    // Yield to the wrapper's click-vs-drag arbiter whenever the wrapper
    // is going to take the gesture:
    //   - edit mode (always)
    //   - test mode WHEN this item is currently selected (right-click
    //     escape hatch — user has said "let me tweak this one," so a
    //     left-drag should move it, not fire MIDI)
    // Otherwise, fire MIDI as normal.
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    if (!live()) return;

    // Guard (SD-5, always on outside edit mode since DEC-216): first tap
    // arms (no fire), second tap within the window fires. Absent guard /
    // edit mode = fire immediately.
    const guarded = !isEditMode() && !!props.item.guard;
    if (guarded && !armed()) {
      armGuard();
      beginPress();
      setTimeout(() => setPressed(false), 120);
      return;
    }
    if (guarded && armed()) disarmGuard(); // second tap — disarm, then fall through to fire

    beginPress();

    const mode = effectiveMode();
    if (mode === 'toggle' && isPresetButton()) {
      // Not `fireToggle`: that is OPBridge's MIDI toggle engine, and this
      // button sends no MIDI. Play if not playing, stop if playing.
      void sendOnce(!on());
    } else if (mode === 'toggle') {
      void fireToggle();
    } else if (mode === 'momentary') {
      // Capture the pointer so pointerup fires on this element even if the
      // finger drags off the button bounds — stuck-on notes are the worst
      // possible failure mode for a live performer.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
      void sendOnce(true);
    } else {
      // trigger — fire ON then OFF in the same tick. DAWs interpret this
      // as a brief blip, which is what scene-launch / scratch-fire wants.
      void sendOnce(true);
      void sendOnce(false);
    }
    // Momentary releases on pointerup (see beginPress note); the other
    // modes flash and release themselves.
    if (mode !== 'momentary') setTimeout(() => setPressed(false), 120);
  }

  function onPointerUp(_e: PointerEvent & { currentTarget: HTMLButtonElement }) {
    const mode = effectiveMode();
    if (mode !== 'momentary') return;
    // Release the VISUAL first and unconditionally. The guards below can
    // flip between pointerdown and pointerup (transport drops, the item
    // gets selected), and if that happened after the press had begun the
    // early return used to strand the button looking held down. The note
    // still needs the guards; the pixels do not.
    endPressWithFloor();
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return; // right-click escape hatch active
    if (!live()) return;
    void sendOnce(false);
  }

  const name = () => props.item.name ?? '';
  const showName = () => !props.item.hideName;
  /** An unnamed one-step Ableton Control shows what it sets ("120 BPM",
   *  "Play", "Rec 1/16") as its face, in every mode (DEC-291): it lights from
   *  Live now, and a lit blank tile tells the operator nothing. '' otherwise. */
  const autoFace = (): string => {
    if (props.item.actionType !== 'ableton' || name()) return '';
    const s = soleAbletonStep();
    return s ? buttonFace(s, { preset: (id) => motionPresetById(id)?.name }) : '';
  };
  /** What the name slot shows: the operator's name, else the auto face. */
  const faceText = (): string => name() || autoFace();

  /**
   * Face text for an Ableton Control button.
   *
   * The MIDI face ("cc 0 · ch 1") is not just unhelpful here, it is WRONG -
   * this button sends no MIDI, and the canvas face is the one surface read
   * at a glance mid-set. A single step names its own value; a chain says how
   * many things it fires, because "140 · 6/8 · 16T" does not fit on a tile
   * and truncating it would be worse than counting.
   */
  const abletonFaceLabel = (): string => {
    const steps = props.item.abletonSteps ?? [];
    if (steps.length === 0) return 'no steps';
    if (steps.length > 1) return `${steps.length} steps`;
    const s = steps[0]!;
    // 'practice' is worth showing: two identical-looking buttons where one
    // drives Live and the other only moves the local playhead is exactly the
    // confusion the target toggle exists to prevent.
    const suffix = 'clock' in s && s.clock === 'practice' ? ' · practice' : '';
    switch (s.type) {
      case 'ableton.tempo':       return `${s.bpm} BPM${suffix}`;
      case 'ableton.timesig':     return `${s.numerator}/${s.denominator}${suffix}`;
      case 'ableton.transport':   return s.action;
      // Distinct face text, not "stop · practice": the two transports do
      // opposite things and must never look like the same control wearing a
      // suffix.
      case 'practice.transport':  return `practice ${s.action}`;
      case 'ableton.recquant':    return `${s.via === 'op-quantize' ? 'op-q' : 'rec'} ${displayRecQuant(s.label)}`;
      case 'ableton.launchquant': return `launch ${displayLaunchQuant(s.label)}`;
      case 'ableton.metronome':   return `metro ${s.mode}`;
      // Says SLAVE, not just "resync". Two buttons that both read
      // "resync" on a dark stage, one of which would touch the master,
      // is the mistake worth spending a word to prevent.
      case 'ableton.resync':      return 'resync slave';
      case 'ableton.sustainkill': return 'sustain kill';
      case 'ableton.endsessionrecord': return 'end session rec';
      case 'opmotion.crossfade':  return s.action === 'flick' ? 'xfade flick' : `xfade to ${crossfadeSide(s.action)?.letter.toLowerCase()}`;
      case 'opmotion.capture':    return s.bus === 'all' ? 'capture' : `capture ${s.bus}`;
      case 'opmotion.land':       return `land ${motionPresetById(s.presetId)?.name ?? 'nothing'}`;
    }
  };
  /**
   * Face text for an OBS Scene button: the scene name, or the reason it
   * cannot work. Truncation is left to CSS - an elided long scene name is
   * still recognisable, whereas a count ("1 scene") would not be.
   */
  const obsFaceLabel = (): string => obsDeadState() ?? (props.item.obsScene ?? '');

  /** A Run Program button's face: the fault, the pending approval, or the
   *  program's registered name. */
  const programFaceLabel = (): string =>
    programDeadState() ?? (programPending() ? 'waiting for OK' : programFaceName());

  /** An Ableton Device button's face: the fault if it has one, else
   *  `Track / Device (2nd)`. Never blank - a device button with no label
   *  would be unidentifiable in a rack of them. */
  const deviceFaceLabel = (): string =>
    deviceDeadState() ?? abletonDeviceSummary(props.item.abletonDevice);

  /** A Light Preset button's face: the fault, else the preset's name. */
  const presetFaceLabel = (): string => presetDeadState() ?? presetFaceName();

  // A missing pedal is a loud dead state (DEC-124/138) — surfaced even
  // outside edit mode, same as PresetChange/SnapshotChange's subText,
  // rather than the button just going mysteriously disabled with no
  // explanation on stage.
  // An OBS dead state is shown OUTSIDE edit mode too, same rule as a
  // missing pedal: a button that cannot fire must say why on stage, not
  // just sit there looking ordinary and doing nothing.
  // An auto face already says what the info line would, so the line stays
  // off rather than printing "120 BPM" twice.
  const showLabel = () => (isEditMode() && !props.item.hideLabel && !autoFace())
    || pedalMissing() || (isObsButton() && obsDeadState() !== null)
    || (isDeviceButton() && deviceDeadState() !== null)
    || (isProgramButton() && (programDeadState() !== null || programPending()))
    || (isPresetButton() && presetDeadState() !== null)
    || (isHueButton() && hueDeadState() !== null);
  /** A Program Change is a one-shot: there is no OFF message to pair with
   *  it, so toggle and momentary are both meaningless. Rather than leave
   *  `mode` half-honoured (a "toggle" pc button would latch its face on a
   *  send it can never undo), pc collapses to trigger everywhere the mode
   *  is read. `mode` stays on the item untouched, so switching back to
   *  cc/note restores whatever the author had chosen. */
  const effectiveMode = (): 'toggle' | 'momentary' | 'trigger' =>
    // An OBS scene switch is a one-shot for the same reason a PC is: there
    // is no message that un-switches a scene. Its FACE still shows state -
    // see showState - because the scene it commands has one.
    (props.item.actionType ?? 'cc') === 'obs' ? 'trigger'
      : (props.item.actionType ?? 'cc') === 'pc' ? 'trigger'
      // Running a program is a one-shot: there is no stop (DEC-524). The
      // face still shows RUNNING - see showState - from OPBridge's readback.
      : (props.item.actionType ?? 'cc') === 'runProgram' ? 'trigger'
      // Recalling a Hue scene is a one-shot; the face lights from OPBridge's readback (the room's active scene).
      : (props.item.actionType ?? 'cc') === 'hueScene' ? 'trigger'
      // Every Ableton Control step is a one-shot on the wire, INCLUDING the
      // metronome: `set_metronome` is a discrete send, and `mode: 'toggle'`
      // on the STEP is resolved in dispatch against the last observed state,
      // not by latching here. So firing is always trigger-shaped. Whether
      // the button DISPLAYS a state is a separate question - see showState.
      : (props.item.actionType === 'ableton') ? 'trigger'
        // An Ableton Device toggle takes the METRONOME's shape, not OBS's -
        // and the difference is worth stating because it looks like a toggle.
        // It genuinely HAS an inverse (turn the device off again), so it is
        // not a one-shot like a scene switch. But the desired value is
        // resolved against the LAST OBSERVED device state at dispatch, not
        // latched here, exactly as `set_metronome` is - so firing is still
        // trigger-shaped and the face's state comes from readback. A local
        // latch would drift the instant a mouse click in Live moved the
        // device, which is the whole defect the indicator arc exists to fix.
        : (props.item.actionType === 'abletonDevice') ? 'trigger'
          : (props.item.mode ?? 'toggle');
  /**
   * Whether the face shows an on/off state.
   *
   * Deliberately NOT the same question as `effectiveMode() === 'toggle'`.
   * A metronome button FIRES as a trigger but HAS a real, externally-owned
   * on/off state worth showing - Live's own click, which Live's transport
   * bar and OPBridge's dashboard can both change without us. Tying display
   * to firing mode would force a false choice between "latches locally and
   * lies" and "shows nothing at all".
   */
  const showState = () => (effectiveMode() === 'toggle' && !(isPresetButton() && presetDeadState() !== null))
    || isMetronomeButton()
    // An OBS button with no scene, no bridge or no readback has no state
    // to show, and an ON/OFF pip under a dead-state face would contradict
    // it. State appears only once the control is genuinely wired.
    || (isObsButton() && obsDeadState() === null)
    // Same rule for a device button: a state pip under a face that reads
    // "pick a computer" would contradict it. State appears only once the
    // control is genuinely wired AND something has been reported.
    || (isDeviceButton() && deviceDeadState() === null)
    // Same rule for a program button: no pip under a dead-state face.
    || (isProgramButton() && programDeadState() === null);
  /**
   * The state pip's text. ON/OFF everywhere except an OBS Scene button,
   * which says ON AIR.
   *
   * Not decoration. "ON" under a camera name is genuinely ambiguous - it
   * reads just as easily as "this source is enabled" as it does "this is
   * what the audience can see", and those are opposite mistakes to make
   * mid-stream. ON AIR is the term every streamer already knows and it can
   * only mean one thing. The app's own ethos is that the novice reach for a
   * thing should work first time; borrowing the domain's word costs one
   * ternary and removes the only reading that could go wrong.
   *
   * Only the ON side changes. A blank OFF would read as a control with no
   * state at all, which is the affordance the pip exists to provide.
   */
  const stateLabel = (): string =>
    on() ? (isObsButton() ? 'ON AIR' : isProgramButton() ? 'RUNNING' : isPresetButton() ? 'PLAYING' : 'ON') : 'OFF';

  const shape = () => props.item.shape ?? 'rect';
  const def = () => shapeDef(shape());
  const clipPath = () => clipPathFor(shape());
  const nameLines = () => splitLabelLines(faceText());
  const nameSize = () => props.item.nameSize ?? 'auto';

  // Names fitted to the pad (DEC-316 / DEC-321): Auto GROWS as well as shrinks, as big as the pad allows, and a name
  // of two or more words goes onto two lines when that is bigger (the grid's fitPadName; HOLD NOTE sat at 16 px on a
  // 200 px pad). A fixed S / M / L / XL keeps its size unless the name would run off. Re-fitted when the pad resizes.
  let nameContainer: HTMLSpanElement | undefined;
  const [shownNameLines, setShownNameLines] = createSignal<string[]>(nameLines());
  // Deferred work (a frame, the fonts) can land after this button was replaced; it must not read its props then
  // (Solid reports a stale accessor: found when switching Guard on remounted the button).
  let disposed = false;
  onCleanup(() => { disposed = true; });
  const refitName = () => {
    if (disposed) return;
    const el = nameContainer, pad = el?.closest('.midi-button') as HTMLElement | null;
    if (!el || !pad) return;
    const lines = fitPadName(pad, el, faceText(), nameSize() === 'auto', !!props.item.icon && props.item.icon !== 'none' && (props.item.iconAt ?? 'above') !== 'corner' && props.item.iconAt !== 'off');
    if (lines) setShownNameLines(lines);
  };
  createEffect(() => {
    void nameLines(); void nameSize(); void props.item.icon; void props.item.iconAt;
    setShownNameLines(nameLines());
    requestAnimationFrame(refitName);
  });
  let ro: ResizeObserver | undefined;
  const observeResize = (el: HTMLSpanElement | undefined) => {
    nameContainer = el;
    if (ro) { ro.disconnect(); ro = undefined; }
    const pad = el?.closest('.midi-button') as HTMLElement | null;
    if (!pad) return;
    ro = new ResizeObserver(() => refitName());
    ro.observe(pad);
    // Fonts landing after the first fit change the widths without a resize (see ButtonGrid.tsx).
    void document.fonts?.ready.then(() => requestAnimationFrame(refitName));
  };
  onCleanup(() => { if (ro) ro.disconnect(); });

  // Inline style handles the per-shape concerns CSS can't express directly:
  // clip-path polygon (varies per shape) and text-inset padding (varies
  // per shape's centroid). Border-radius still lives in CSS for the
  // rect/curve family (see .midi-button[data-shape=...] blocks).
  const buttonStyle = (): Record<string, string> => {
    const style: Record<string, string> = {};
    const cp = clipPath();
    if (cp) style['clip-path'] = cp;
    const inset = def().textInset;
    if (inset) {
      style.padding = `${inset.top}% ${inset.right}% ${inset.bottom}% ${inset.left}%`;
    }
    return style;
  };

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      // A running timeline lights the face too (DEC-269). ORed into the
      // ATTRIBUTE only: `on()` also feeds the Display mirror, the state label
      // and the light-preset send decision, none of which a timeline flash
      // should touch.
      data-on={on() || timelineLit(props.item.id) ? 'true' : 'false'}
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={armed() ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape={shape()}
      data-family={def().family}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      disabled={!live()}
      style={buttonStyle()}
    >
      <PadDecor item={props.item} />
      {showLabel() && (
        <span class="midi-button-label">
          {pedalMissing() ? (
            'device removed'
          ) : (props.item.actionType ?? 'cc') === 'obs' ? (
            // Never "cc N": this button emits no MIDI. The scene name IS the
            // useful face, and a dead state replaces it rather than sitting
            // beside it - on a dark stage the face is read in one glance.
            obsFaceLabel()
          ) : (props.item.actionType ?? 'cc') === 'runProgram' ? (
            // No MIDI leaves this button either; the program is the face.
            programFaceLabel()
          ) : (props.item.actionType ?? 'cc') === 'lightPreset' ? (
            // Nor this one: the preset (or why it cannot play) is the face.
            presetFaceLabel()
          ) : (props.item.actionType ?? 'cc') === 'hueScene' ? (
            // Nor a Hue scene: its room and name, or why it cannot be recalled.
            hueFaceLabel()
          ) : (props.item.actionType ?? 'cc') === 'abletonDevice' ? (
            // Same reasoning as OBS above: no MIDI leaves this button, so a
            // "cc N" face would be a lie. `Track / Device`, or the fault.
            deviceFaceLabel()
          ) : (props.item.actionType ?? 'cc') === 'ableton' ? (
            // NOT "cc N · ch N": this button emits no MIDI, so the MIDI face
            // would be a lie on the one surface the operator reads at a
            // glance mid-set. Show what it actually does.
            abletonFaceLabel()
          ) : (props.item.actionType ?? 'cc') === 'noteon' ? (
            <>note <b>{props.item.note ?? 60}</b> · ch <b>{resolvedChannel() + 1}</b></>
          ) : (props.item.actionType ?? 'cc') === 'pc' ? (
            <>pc <b>{props.item.program ?? 0}</b> · ch <b>{resolvedChannel() + 1}</b></>
          ) : (
            <>cc <b>{props.item.ccNumber}</b> · ch <b>{resolvedChannel() + 1}</b></>
          )}
        </span>
      )}
      {showName() && faceText() && (
        <span
          class="midi-button-name"
          data-name-size={nameSize()}
          ref={(el) => observeResize(el)}
        >
          <For each={shownNameLines()}>
            {(line) => <span class="face-line">{line}</span>}
          </For>
        </span>
      )}
      {showState() && (
        <span class="midi-button-state">{stateLabel()}</span>
      )}
      {hueSwatch() && <i class="hue-face-swatch" aria-hidden="true" style={{ background: hueSwatch()! }} />}
    </button>
  );
}
